import type { PayrollReconciliationItemV1 } from "@impetusos/contracts"
import type { ReactElement } from "react"

export interface PayrollReconciliationWorkspaceProps {
  status: "loading" | "ready" | "empty" | "error" | "denied"
  items: readonly PayrollReconciliationItemV1[]
  onRetry: () => void
  onAssign: (item: PayrollReconciliationItemV1) => void
  onRequestRetry: (item: PayrollReconciliationItemV1) => void
  onResolve: (item: PayrollReconciliationItemV1) => void
}

const label = (value: string): string =>
  value.replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase())

export function PayrollReconciliationWorkspace({
  status,
  items,
  onRetry,
  onAssign,
  onRequestRetry,
  onResolve,
}: PayrollReconciliationWorkspaceProps): ReactElement {
  if (status === "loading") {
    return <p aria-live="polite">Loading payroll reconciliation exceptions…</p>
  }
  if (status === "denied") {
    return <p role="alert">You do not have access to payroll reconciliation.</p>
  }
  if (status === "error") {
    return (
      <div role="alert">
        <p>Payroll reconciliation is temporarily unavailable.</p>
        <button type="button" onClick={onRetry}>Try again</button>
      </div>
    )
  }
  if (status === "empty" || items.length === 0) {
    return (
      <section aria-labelledby="payroll-reconciliation-title">
        <h2 id="payroll-reconciliation-title">Payroll reconciliation</h2>
        <p>No payroll reconciliation exceptions need review.</p>
      </section>
    )
  }

  return (
    <section id="payroll-reconciliation" aria-labelledby="payroll-reconciliation-title">
      <header>
        <p>Payroll · Worklio to Frappe</p>
        <h2 id="payroll-reconciliation-title">Payroll reconciliation</h2>
        <p>
          Review input, run, settlement, accounting, and document mismatches.
          These actions never silently change Worklio or Frappe source records.
        </p>
      </header>
      <div role="region" aria-label="Payroll reconciliation exceptions" tabIndex={0}>
        <table>
          <caption>{items.length} exceptions require controlled review</caption>
          <thead>
            <tr>
              <th scope="col">Kind</th>
              <th scope="col">Reason</th>
              <th scope="col">Expected</th>
              <th scope="col">Observed</th>
              <th scope="col">State</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.exceptionId}>
                <th scope="row">{label(item.mismatchKind)}</th>
                <td>{label(item.reasonCode)}</td>
                <td><code>{item.expectedRef}</code></td>
                <td><code>{item.observedRef}</code></td>
                <td>{label(item.state)}</td>
                <td>
                  <button
                    type="button"
                    onClick={() => onAssign(item)}
                    disabled={item.state === "resolved"}
                    aria-label={`Assign ${item.mismatchKind} mismatch`}
                  >
                    Assign
                  </button>
                  <button
                    type="button"
                    onClick={() => onRequestRetry(item)}
                    disabled={item.state === "retry_pending" || item.state === "resolved"}
                    aria-label={`Request retry for ${item.mismatchKind} mismatch`}
                  >
                    Request retry
                  </button>
                  <button
                    type="button"
                    onClick={() => onResolve(item)}
                    disabled={item.state === "resolved"}
                    aria-label={`Resolve ${item.mismatchKind} mismatch`}
                  >
                    Resolve
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p role="note">
        Worklio remains authoritative for payroll outcomes. Every assignment,
        retry request, and resolution is human-attributed and audited.
      </p>
    </section>
  )
}
